import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd } from '../locale.js';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { Pencil, Trash2 } from 'lucide-react';
import { Callout, PageHeader } from '../ui/index.js';

type ModifierGroupRow = Database['public']['Tables']['modifier_groups']['Row'];
type ModifierOptionRow = Database['public']['Tables']['modifier_options']['Row'];
type ProductRow = Database['public']['Tables']['products']['Row'];

export function ModifiersManager({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const empty = t('common.emptyDash');
  const AMD = (n: number) => {
    if (n === 0) return empty;
    const body = formatAmd(Math.abs(n), i18n.language);
    return `${n > 0 ? '+' : '−'}${body}`;
  };
  const [groups, setGroups] = useState<ModifierGroupRow[]>([]);
  const [options, setOptions] = useState<ModifierOptionRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  // product_id set for each group_id
  const [attachments, setAttachments] = useState<Map<string, Set<string>>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // new group form
  const [gName, setGName] = useState('');
  const [gMin, setGMin] = useState('0');
  const [gMax, setGMax] = useState('1');
  const [gSaving, setGSaving] = useState(false);

  // inline group edit
  const [gEditId, setGEditId] = useState<string | null>(null);
  const [gEdit, setGEdit] = useState({ name: '', min: '0', max: '1' });
  const [gEditSaving, setGEditSaving] = useState(false);

  // new option per group: groupId -> {name, delta, kind}
  type OptDraft = { name: string; delta: string; kind: 'fixed' | 'percent' };
  const [optForm, setOptForm] = useState<Record<string, OptDraft>>({});
  const draft = (groupId: string): OptDraft =>
    optForm[groupId] ?? { name: '', delta: '', kind: 'fixed' };

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const [gRes, oRes, pRes, pmgRes] = await Promise.all([
      supabase.from('modifier_groups').select('*').order('name'),
      supabase.from('modifier_options').select('*').order('name'),
      supabase.from('products').select('id, name').eq('active', true).order('name'),
      supabase.from('product_modifier_groups').select('product_id, group_id'),
    ]);
    if (gRes.error) {
      setError(gRes.error.message);
      setLoading(false);
      return;
    }
    if (oRes.error) {
      setError(oRes.error.message);
      setLoading(false);
      return;
    }
    setGroups(gRes.data);
    setOptions(oRes.data);
    if (!pRes.error) setProducts(pRes.data as ProductRow[]);
    // Build attachment map: group_id -> Set<product_id>
    const map = new Map<string, Set<string>>();
    for (const row of pmgRes.data ?? []) {
      if (!map.has(row.group_id)) map.set(row.group_id, new Set());
      map.get(row.group_id)!.add(row.product_id);
    }
    setAttachments(map);
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Groups ──────────────────────────────────────────────────────────────────

  async function addGroup() {
    if (!supabase || !clientId) return;
    if (!gName.trim()) {
      setError(t('modifiers.groupNameRequired'));
      return;
    }
    setGSaving(true);
    setError(null);
    const { error: err } = await supabase.from('modifier_groups').insert({
      client_id: clientId,
      name: gName.trim(),
      min: Number(gMin) || 0,
      max: Math.max(1, Number(gMax) || 1),
    });
    setGSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setGName('');
    setGMin('0');
    setGMax('1');
    void load();
  }

  async function saveGroupEdit(id: string) {
    if (!supabase) return;
    setGEditSaving(true);
    setError(null);
    const { error: err } = await supabase
      .from('modifier_groups')
      .update({
        name: gEdit.name.trim(),
        min: Number(gEdit.min) || 0,
        max: Math.max(1, Number(gEdit.max) || 1),
      })
      .eq('id', id);
    setGEditSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setGEditId(null);
    void load();
  }

  async function deleteGroup(id: string) {
    if (!supabase) return;
    if (!confirm(t('modifiers.deleteConfirm'))) return;
    const { error: err } = await supabase.from('modifier_groups').delete().eq('id', id);
    if (err) setError(err.message);
    else void load();
  }

  // ── Options ─────────────────────────────────────────────────────────────────

  async function addOption(groupId: string) {
    if (!supabase || !clientId) return;
    const f = draft(groupId);
    if (!f.name.trim()) {
      setError(t('modifiers.optionNameRequired'));
      return;
    }
    const value = Number(f.delta);
    if (!Number.isFinite(value)) {
      setError(t('modifiers.deltaInvalid'));
      return;
    }
    const { error: err } = await supabase.from('modifier_options').insert({
      client_id: clientId,
      group_id: groupId,
      name: f.name.trim(),
      price_delta: f.kind === 'fixed' ? value : 0,
      delta_percent: f.kind === 'percent' ? value : null,
    });
    if (err) {
      setError(err.message);
      return;
    }
    setOptForm((prev) => ({ ...prev, [groupId]: { name: '', delta: '', kind: 'fixed' } }));
    void load();
  }

  async function deleteOption(id: string) {
    if (!supabase) return;
    const { error: err } = await supabase.from('modifier_options').delete().eq('id', id);
    if (err) setError(err.message);
    else void load();
  }

  // ── Attachments ─────────────────────────────────────────────────────────────

  async function attachProduct(groupId: string, productId: string) {
    if (!supabase || !clientId || !productId) return;
    const { error: err } = await supabase.from('product_modifier_groups').insert({
      client_id: clientId,
      group_id: groupId,
      product_id: productId,
    });
    if (err) setError(err.message);
    else void load();
  }

  async function detachProduct(groupId: string, productId: string) {
    if (!supabase) return;
    const { error: err } = await supabase
      .from('product_modifier_groups')
      .delete()
      .eq('group_id', groupId)
      .eq('product_id', productId);
    if (err) setError(err.message);
    else void load();
  }

  function formatDelta(o: ModifierOptionRow) {
    if (o.delta_percent != null) {
      const n = Number(o.delta_percent);
      if (n === 0) return `0%`;
      return `${n > 0 ? '+' : '−'}${Math.abs(n)}%`;
    }
    return AMD(o.price_delta);
  }

  const groupOptions = (groupId: string) => options.filter((o) => o.group_id === groupId);
  const attachedProducts = (groupId: string) =>
    [...(attachments.get(groupId) ?? [])]
      .map((pid) => products.find((p) => p.id === pid))
      .filter(Boolean) as ProductRow[];
  const unattachedProducts = (groupId: string) =>
    products.filter((p) => !attachments.get(groupId)?.has(p.id));

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('modifiers.title')}
        subtitle={t('modifiers.subtitle')}
      />

      <div className="info-box" style={{ marginBottom: 24 }}>
        <strong>{t('modifiers.howTitle')}</strong>
        <ol style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.8 }}>
          <li>{t('modifiers.step1')}</li>
          <li>{t('modifiers.step2')}</li>
          <li>{t('modifiers.step3')}</li>
        </ol>
        <p style={{ margin: '10px 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
          {t('modifiers.footer')}
        </p>
      </div>

      {error && <Callout tone="danger">{error}</Callout>}

      {/* ── Create group ─────────────────────────────────────────── */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">{t('modifiers.addGroup')}</span>
        </div>
        <div className="add-form">
          <input
            placeholder={t('modifiers.namePlaceholder')}
            value={gName}
            onChange={(e) => setGName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void addGroup()}
            style={{ minWidth: 220 }}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            {t('modifiers.min')}
            <input
              type="number"
              min="0"
              value={gMin}
              onChange={(e) => setGMin(e.target.value)}
              style={{ width: 56 }}
            />
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            {t('modifiers.max')}
            <input
              type="number"
              min="1"
              value={gMax}
              onChange={(e) => setGMax(e.target.value)}
              style={{ width: 56 }}
            />
          </label>
          <button className="btn" onClick={addGroup} disabled={gSaving}>
            {gSaving ? '…' : t('modifiers.submit')}
          </button>
        </div>
      </div>

      {/* ── Groups list ──────────────────────────────────────────── */}
      {loading ? (
        <div className="empty-state">{t('common.loading')}</div>
      ) : groups.length === 0 ? (
        <div className="empty-state">{t('modifiers.empty')}</div>
      ) : (
        groups.map((g) => (
          <div key={g.id} className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-header">
              {gEditId === g.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <input
                    className="table-edit-input"
                    value={gEdit.name}
                    onChange={(e) => setGEdit({ ...gEdit, name: e.target.value })}
                    autoFocus
                    style={{ minWidth: 180 }}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    {t('modifiers.min')}
                    <input
                      className="table-edit-input"
                      type="number"
                      min="0"
                      value={gEdit.min}
                      onChange={(e) => setGEdit({ ...gEdit, min: e.target.value })}
                      style={{ width: 50 }}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                    {t('modifiers.max')}
                    <input
                      className="table-edit-input"
                      type="number"
                      min="1"
                      value={gEdit.max}
                      onChange={(e) => setGEdit({ ...gEdit, max: e.target.value })}
                      style={{ width: 50 }}
                    />
                  </label>
                  <div className="row-actions">
                    <button
                      className="btn btn-sm"
                      onClick={() => void saveGroupEdit(g.id)}
                      disabled={gEditSaving}
                    >
                      {gEditSaving ? '…' : t('common.save')}
                    </button>
                    <button className="btn-ghost btn-sm" onClick={() => setGEditId(null)}>
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <span className="panel-title">
                    {g.name}
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: 'var(--text-muted)',
                        fontWeight: 400,
                      }}
                    >
                      {t('modifiers.pickRule', {
                        rule: g.min === g.max ? g.min : `${g.min}–${g.max}`,
                      })}
                      {g.min > 0 ? t('modifiers.required') : t('modifiers.optional')}
                    </span>
                  </span>
                  <div className="row-actions">
                    <button
                      className="btn-icon"
                      onClick={() => {
                        setGEditId(g.id);
                        setGEdit({ name: g.name, min: String(g.min), max: String(g.max) });
                      }}
                      title={t('common.edit')}
                    >
                      <Pencil className="ui-i" />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => void deleteGroup(g.id)}
                      title={t('common.delete')}
                    >
                      <Trash2 className="ui-i" />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Options */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--text-muted)',
                }}
              >
                {t('modifiers.options')}
              </p>
              <div className="add-form" style={{ marginBottom: 10 }}>
                <input
                  placeholder={t('modifiers.optionName')}
                  value={draft(g.id).name}
                  onChange={(e) =>
                    setOptForm((prev) => {
                      const current = prev[g.id] ?? { name: '', delta: '', kind: 'fixed' as const };
                      return { ...prev, [g.id]: { ...current, name: e.target.value } };
                    })
                  }
                  onKeyDown={(e) => e.key === 'Enter' && void addOption(g.id)}
                  style={{ minWidth: 180 }}
                />
                <div className="ui-segment ui-segment--sm" role="group" aria-label={t('modifiers.deltaKind')}>
                  <button
                    type="button"
                    aria-pressed={draft(g.id).kind === 'fixed'}
                    onClick={() =>
                      setOptForm((prev) => {
                        const current = prev[g.id] ?? { name: '', delta: '', kind: 'fixed' as const };
                        return { ...prev, [g.id]: { ...current, kind: 'fixed' } };
                      })
                    }
                  >
                    {t('modifiers.kindFixed')}
                  </button>
                  <button
                    type="button"
                    aria-pressed={draft(g.id).kind === 'percent'}
                    onClick={() =>
                      setOptForm((prev) => {
                        const current = prev[g.id] ?? { name: '', delta: '', kind: 'fixed' as const };
                        return { ...prev, [g.id]: { ...current, kind: 'percent' } };
                      })
                    }
                  >
                    {t('modifiers.kindPercent')}
                  </button>
                </div>
                <input
                  placeholder={
                    draft(g.id).kind === 'percent'
                      ? t('modifiers.deltaPlaceholderPercent')
                      : t('modifiers.deltaPlaceholder')
                  }
                  type="number"
                  step="any"
                  value={draft(g.id).delta}
                  onChange={(e) =>
                    setOptForm((prev) => {
                      const current = prev[g.id] ?? { name: '', delta: '', kind: 'fixed' as const };
                      return { ...prev, [g.id]: { ...current, delta: e.target.value } };
                    })
                  }
                  style={{ maxWidth: 180 }}
                />
                <button className="btn btn-sm" onClick={() => void addOption(g.id)}>
                  {t('modifiers.addOption')}
                </button>
              </div>
              {groupOptions(g.id).length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
                  {t('modifiers.noOptions')}
                </p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('modifiers.colOption')}</th>
                      <th>{t('modifiers.colPriceDelta')}</th>
                      <th style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupOptions(g.id).map((o) => (
                      <tr key={o.id}>
                        <td>{o.name}</td>
                        <td
                          style={{
                            color:
                              (o.delta_percent ?? o.price_delta) === 0
                                ? 'var(--text-muted)'
                                : 'var(--text)',
                          }}
                        >
                          {formatDelta(o)}
                        </td>
                        <td>
                          <button
                            className="btn-icon danger"
                            onClick={() => void deleteOption(o.id)}
                            title={t('common.delete')}
                          >
                            <Trash2 className="ui-i" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Product attachments */}
            <div style={{ padding: '12px 16px' }}>
              <p
                style={{
                  margin: '0 0 8px',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--text-muted)',
                }}
              >
                {t('modifiers.attached')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {attachedProducts(g.id).map((p) => (
                  <span key={p.id} className="modifier-tag">
                    {p.name}
                    <button
                      onClick={() => void detachProduct(g.id, p.id)}
                      title={t('modifiers.removeTitle', { name: p.name })}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {attachedProducts(g.id).length === 0 && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {t('modifiers.noAttached')}
                  </span>
                )}
              </div>
              {unattachedProducts(g.id).length > 0 && (
                <select
                  style={{ fontSize: 13, padding: '4px 8px' }}
                  value=""
                  onChange={(e) => {
                    if (e.target.value) void attachProduct(g.id, e.target.value);
                  }}
                >
                  <option value="">{t('modifiers.attachPlaceholder')}</option>
                  {unattachedProducts(g.id).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        ))
      )}
    </section>
  );
}
